"use client";

import * as React from "react";
import * as LabelPrimitive from "@radix-ui/react-label";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../../../lib/utils";

const labelVariants = cva(
  "leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-semantic-secondary-foreground-normal-default group-hover:text-semantic-secondary-foreground-normal-hovered group-active:text-semantic-secondary-foreground-normal-pressed",
  {
    defaultVariants: {
      size: "label-1-normal---regular",
    },
    variants: {
      size: {
        "label-1-normal---regular":
          "typography-semantic-label-1-normal---regular",
        "label-1-normal---medium":
          "typography-semantic-label-1-normal---medium",
        "label-2-normal---regular":
          "typography-semantic-label-2-normal---regular",
        "label-2-regular": "typography-semantic-label-2-regular",
        "body-1-reading---regular":
          "typography-semantic-body-1-reading---regular",
        "body-1-reading---bold": "typography-semantic-body-1-reading---bold",
        "body-1-normal---bold": "typography-semantic-body-1-normal---bold",
        "body-2-normal---bold": "typography-semantic-body-2-normal---bold",
        "body-2-normal---medium": "typography-semantic-body-2-normal---medium",
        "heading-2-bold": "typography-semantic-heading-2-bold",
        "heading-3-bold": "typography-semantic-heading-3-bold",
        "caption-1-regular": "typography-semantic-caption-1-regular",
      },
    },
  }
);

type LabelSize =
  | "label-1-normal---regular"
  | "label-1-normal---medium"
  | "label-2-normal---regular"
  | "label-2-regular"
  | "body-1-reading---regular"
  | "body-1-reading---bold"
  | "body-1-normal---bold"
  | "body-2-normal---bold"
  | "body-2-normal---medium"
  | "heading-2-bold"
  | "heading-3-bold"
  | "caption-1-regular";

interface LabelProps
  extends React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>,
    VariantProps<typeof labelVariants> {
  required?: boolean;
  size?: LabelSize;
}

const Label = React.forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  LabelProps
>(({ className, required, size, children, ...props }, ref) => (
  <LabelPrimitive.Root
    ref={ref}
    className={cn(labelVariants({ size }), className)}
    {...props}
  >
    {children}
    {required && (
      <span className="text-semantic-error-global-global-default ml-sp40">
        *
      </span>
    )}
  </LabelPrimitive.Root>
));
Label.displayName = LabelPrimitive.Root.displayName;

export { Label };
